import {
  ClientPortalAvailableDefaultLanguageEnum,
  ClientPortalBlockFieldDisplayEnum,
  ClientPortalBlockFieldModel,
  ClientPortalBlockLayoutModel,
  ClientPortalBlockLayoutWidgetEnum,
  ClientPortalBlockTypeEnum,
  ClientPortalCollaborationEnum,
  ClientPortalModel,
  ClientPortalSettingModel,
  ClientPortalSettingStylingModel,
  ClientPortalSettingWordingModel,
  ClientPortalStatusEnum,
  ClientPortalStepTypeEnum,
  ClientPortalTypeEnum,
  PropertyMappingEntityEnum,
  PropertyMappingTypeEnum,
} from '../../../shared/models';

export interface DefaultClientPortalWordingCheck {
  label: string;
  description: string | null;
}

export interface DefaultClientPortalWording extends Record<string, unknown> {
  checks: Record<string, DefaultClientPortalWordingCheck>;
}

// Configuration for custom steps (fully configurable)
export interface DefaultClientPortalCustomStep {
  type: typeof ClientPortalStepTypeEnum.custom;
  title: string;
  description?: string;
  key?: string; // If not provided, will be generated from title
  position: number;
  isVisible?: boolean;
  blocks: (
    | Pick<
        ClientPortalBlockFieldModel,
        | 'type'
        | 'description'
        | 'position'
        | 'mapping'
        | 'label'
        | 'display'
        | 'isRequired'
      >
    | (Pick<ClientPortalBlockLayoutModel, 'type' | 'position' | 'widget'> & {
        key?: string;
        description?: string;
        label?: string;
      })
  )[];
}

// Configuration for default step overrides (title/description + extra blocks)
export interface DefaultClientPortalDefaultStepOverride {
  type: Exclude<
    ClientPortalStepTypeEnum,
    typeof ClientPortalStepTypeEnum.custom
  >;
  title?: string; // Override default title
  description?: string; // Override default description
  extraBlocks?: (
    | Pick<
        ClientPortalBlockFieldModel,
        | 'type'
        | 'description'
        | 'position'
        | 'mapping'
        | 'label'
        | 'display'
        | 'isRequired'
      >
    | (Pick<ClientPortalBlockLayoutModel, 'type' | 'position' | 'widget'> & {
        description?: string;
        label?: string;
        key?: string;
      })
  )[]; // Additional blocks to add to default ones
}

export type DefaultClientPortalStepConfig =
  | DefaultClientPortalCustomStep
  | DefaultClientPortalDefaultStepOverride;

export type DefaultClientPortalConfig = Pick<
  ClientPortalModel,
  'name' | 'type' | 'status' | 'defaultLanguage'
> & {
  templateName?: string;
  setting: Pick<
    ClientPortalSettingModel,
    'collaboration' | 'isCaseCreationAllowed' | 'magicLinkTTL'
  > & {
    styling: Pick<
      ClientPortalSettingStylingModel,
      'showDotfileLogo' | 'colorsMain'
    >;
    wording?: ClientPortalSettingWordingModel;
  };
  steps?: DefaultClientPortalStepConfig[]; // Optional step customizations
};

export const DEFAULT_CLIENT_PORTALS: DefaultClientPortalConfig[] = [
  {
    name: 'Simple KYB',
    type: ClientPortalTypeEnum.KYB,
    status: ClientPortalStatusEnum.online,
    defaultLanguage: ClientPortalAvailableDefaultLanguageEnum.en,
    templateName: 'Simple KYB Template',
    setting: {
      collaboration: ClientPortalCollaborationEnum.existing_and_add_new,
      isCaseCreationAllowed: true,
      magicLinkTTL: 7000,
      styling: {
        showDotfileLogo: true,
        colorsMain: '#000000',
      },
      wording: {
        checks: {
          document__registration_certificate: {
            label: 'Registration certificate',
            description:
              '<p>Please provide a document not older than 3 months</p>',
          },
        },
      },
    },
    steps: [
      // Custom welcome step with structure information
      {
        type: ClientPortalStepTypeEnum.custom,
        key: 'money_wiz_onboarding',
        title: 'MoneyWiz Onboarding',
        description:
          '<p><b>Thank you for starting your journey with us!</b></p><p>Within this onboarding questionnaire, we will ask you to provide your business information and upload documents required to verify your business.</p>',
        position: -999,
        blocks: [
          {
            type: ClientPortalBlockTypeEnum.layout,
            widget: ClientPortalBlockLayoutWidgetEnum.divider,
            position: 1,
          },
          {
            type: ClientPortalBlockTypeEnum.layout,
            widget: ClientPortalBlockLayoutWidgetEnum.text,
            key: 'structure',
            description:
              '<p><b>Structure</b></p><p>1. General Company Information</p><p>2. License (if applicable)</p><p>3. Expected Volumes</p><p>4. Banking Information</p><p>6. UBOs and Directors</p><p>7. Documents</p>',
            position: 2,
          },
          {
            type: ClientPortalBlockTypeEnum.layout,
            widget: ClientPortalBlockLayoutWidgetEnum.divider,
            position: 3,
          },
          {
            type: ClientPortalBlockTypeEnum.layout,
            widget: ClientPortalBlockLayoutWidgetEnum.text,
            key: 'welcome',
            description:
              '<p><b>Time Commitment: </b>Set aside 5 minutes to complete the questionnaire and upload the necessary documents.</p><p></p><p><b>Resuming Questionnaire:</b> Continue with the questionnaire using:</p><ul><li><p>The link sent to your email after selecting "save for later".</p></li><li><p>The original link shared with you.</p></li></ul><p></p><p><b>Support: </b>For issues, contact our support team at <a target="_blank" rel="noopener noreferrer nofollow" href="mailto:onboarding@getivy.io">onboarding@moneywiz.com</a></p>',
            position: 4,
          },
        ],
      },
      // Override company_edit step
      {
        type: ClientPortalStepTypeEnum.company_edit,
        title: 'Verify your company information',
        extraBlocks: [
          {
            type: ClientPortalBlockTypeEnum.layout,
            widget: ClientPortalBlockLayoutWidgetEnum.text,
            key: 'company_info',
            description: '<p><b>A | Company Information</b></p>',
            position: -999,
          },
          // Add missing company fields to match exact production data
          {
            type: ClientPortalBlockTypeEnum.field,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.company,
              key: 'tax_identification_number',
            },
            label: 'Tax identification number',
            description:
              '<p><i>Tax ID (if applicable) and VAT (if applicable)</i></p>',
            position: 5,
            isRequired: false,
            display: null,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.company,
              key: 'registration_date',
            },
            label: 'Registration date',
            description: null,
            position: 6,
            isRequired: false,
            display: null,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.company,
              key: 'entity_legal_form',
            },
            label: 'Legal form',
            description: null,
            position: 7,
            isRequired: false,
            display: null,
          },
          {
            type: ClientPortalBlockTypeEnum.layout,
            widget: ClientPortalBlockLayoutWidgetEnum.text,
            key: 'business_activity',
            description: '<p><b>B | Business Activity</b></p>',
            position: 8,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.company,
              key: 'classifications',
            },
            label: 'Classifications',
            description: null,
            position: 9,
            isRequired: false,
            display: null,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.company,
              key: 'website_url',
            },
            label: 'Website URL',
            description: null,
            position: 10,
            isRequired: false,
            display: null,
          },
        ],
      },
      // Override affiliated_companies_edit step to hide it
      {
        type: ClientPortalStepTypeEnum.affiliated_companies_edit,
        // This step appears to be hidden in production (is_visible: false)
        // We'll leave it as default since we can't control visibility through our current interface
      },
      // Override individuals_edit step
      {
        type: ClientPortalStepTypeEnum.individuals_edit,
        title: 'Check the list of individuals related to your company',
        description:
          '<p><b>Please add the following people:</b><br>1. Directors</p><p>2. Authorized Signatories</p><p>3. UBOs (holding directly or indirectly <b>25% or more</b> of the shares or voting rights of the company<br><br><i>If there is no UBO, add a Person with Significant Control (PSC) such as the CEO, MD, Chairman of the Board, etc.</i></p>',
        extraBlocks: [
          // Only add blocks that are NOT in the default step definition
          {
            type: ClientPortalBlockTypeEnum.field,
            position: 3.5,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.individual,
              key: 'is_beneficial_owner',
            },
            label: 'Is beneficial owner',
            description: null,
            isRequired: false,
            display: ClientPortalBlockFieldDisplayEnum.yes_no,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            position: 6,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.individual,
              key: 'birth_date',
            },
            label: 'Birth date',
            description: null,
            isRequired: false,
            display: null,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            position: 8,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.individual,
              key: 'birth_country',
            },
            label: 'Birth country',
            description: null,
            isRequired: false,
            display: null,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            position: 9,
            mapping: {
              type: PropertyMappingTypeEnum.custom,
              entity: PropertyMappingEntityEnum.individual,
              key: 'ownership_type',
            },
            label: 'Ownership type',
            description: null,
            isRequired: false,
            display: ClientPortalBlockFieldDisplayEnum.dropdown,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            position: 10,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.individual,
              key: 'ownership_percentage',
            },
            label: 'Ownership percentage',
            description: null,
            isRequired: false,
            display: null,
          },
          // Layout block - divider (no manual key)
          {
            type: ClientPortalBlockTypeEnum.layout,
            position: 11,
            widget: ClientPortalBlockLayoutWidgetEnum.divider,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            position: 12,
            mapping: {
              type: PropertyMappingTypeEnum.custom,
              entity: PropertyMappingEntityEnum.individual,
              key: 'is_pep',
            },
            label: 'Is this a Politically Exposed Person (PEP)?',
            description: null,
            isRequired: false,
            display: ClientPortalBlockFieldDisplayEnum.yes_no,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            position: 13,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.individual,
              key: 'address',
            },
            label: 'Residence country',
            description: null,
            isRequired: false,
            display: null,
          },
        ],
      },
      // Override business_contact step
      {
        type: ClientPortalStepTypeEnum.business_contact,
        title: 'Primary Contact Person',
        description: '<p>Please select or add the primary contact person.</p>',
      },
    ],
  },
  {
    name: 'Simple KYC',
    type: ClientPortalTypeEnum.KYC,
    status: ClientPortalStatusEnum.online,
    defaultLanguage: ClientPortalAvailableDefaultLanguageEnum.en,
    templateName: 'Simple KYC Template',
    setting: {
      collaboration: ClientPortalCollaborationEnum.none,
      isCaseCreationAllowed: true,
      magicLinkTTL: 15, // 15 minutes
      styling: {
        showDotfileLogo: true,
        colorsMain: '#2647EA',
      },
      wording: {
        checks: {
          id_document: {
            label: 'Identity document',
            description: null,
          },
          id_verification: {
            label: 'Identity verification',
            description: null,
          },
          document__bank_statement: {
            label: 'Bank Statements',
            description: null,
          },
          electronic_signature__power_of_attorney: {
            label: 'Power of Attorney',
            description: null,
          },
          document__signed_corporate_structure_chart: {
            label: 'Signed Corporate Structure Chart',
            description: null,
          },
        },
      },
    },
    steps: [
      // Override individual_edit step to add address and birth_date fields
      {
        type: ClientPortalStepTypeEnum.individual_edit,
        extraBlocks: [
          {
            type: ClientPortalBlockTypeEnum.field,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.individual,
              key: 'address',
            },
            label: 'Address',
            description: null,
            position: 4,
            isRequired: false,
            display: null,
          },
          {
            type: ClientPortalBlockTypeEnum.field,
            mapping: {
              type: PropertyMappingTypeEnum.default,
              entity: PropertyMappingEntityEnum.individual,
              key: 'birth_date',
            },
            label: 'Birth date',
            description: null,
            position: 5,
            isRequired: false,
            display: null,
          },
        ],
      },
    ],
  },
];
